import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAtlas } from "@/context/AtlasContext";
import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { FormModal } from "@/components/shared/FormModal";
import { StatusPill } from "@/components/shared/StatusPill";
import { Button } from "@/components/ui/button";
import { Megaphone, Plus, Users } from "lucide-react";
import { toast } from "sonner";

const CHANNELS = ["email", "social", "events", "web", "direct_mail", "phone", "paid_ads"].map((v) => ({ value: v, label: v.replace(/_/g, " ") }));

export default function Campaigns() {
  const { has } = useAtlas();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [open, setOpen] = useState(false);
  const canEdit = has("marketing.campaigns.edit");
  const load = () => api.get("/marketing/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async (v) => { await api.post("/marketing/campaigns", { ...v, budget: Number(v.budget) || 0, channels: v.channels || [] }); toast.success("Campaign created"); load(); };

  return (
    <div>
      <PageHeader title="Campaigns" subtitle="Audiences reference shared Atlas People & Organizations">
        {canEdit && <Button onClick={() => setOpen(true)} data-testid="campaign-create-button"><Plus className="w-4 h-4 mr-1" /> New Campaign</Button>}
      </PageHeader>
      {campaigns.length === 0 ? (
        <EmptyState icon={Megaphone} title="No campaigns yet" description="Create a campaign, then add People or Organizations to its audience." actionLabel={canEdit ? "New Campaign" : undefined} onAction={() => setOpen(true)} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="marketing-campaigns-grid">
          {campaigns.map((c) => (
            <button key={c.id} onClick={() => navigate(`/marketing/campaigns/${c.id}`)} data-testid={`campaign-card-${c.id}`}
              className="text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-blue-300 hover:shadow-md transition-all">
              <div className="flex items-start justify-between"><span className="font-heading font-semibold text-slate-900">{c.name}</span><StatusPill value={c.status} /></div>
              <p className="text-sm text-slate-500 mt-1 line-clamp-2 min-h-[40px]">{c.purpose || "No purpose set"}</p>
              <div className="flex gap-1 flex-wrap mt-2">{(c.channels || []).map((ch) => <span key={ch} className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-600 px-2 py-0.5 rounded capitalize">{ch.replace(/_/g, " ")}</span>)}</div>
              <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-3"><Users className="w-3 h-3" />{c.audience_count} in audience</div>
            </button>
          ))}
        </div>
      )}
      <FormModal open={open} onOpenChange={setOpen} title="New Campaign" testid="campaign-form-modal" onSubmit={create}
        fields={[
          { name: "name", label: "Campaign name", required: true },
          { name: "purpose", label: "Purpose", type: "textarea" },
          { name: "status", label: "Status", type: "select", default: "draft", options: ["draft", "active", "paused", "done"].map((v) => ({ value: v, label: v })) },
          { name: "channels", label: "Channels", type: "multiselect", options: CHANNELS },
          { name: "budget", label: "Budget ($)", type: "number", default: 0 },
          { name: "start_date", label: "Start date", type: "date" },
          { name: "end_date", label: "End date", type: "date" },
        ]} />
    </div>
  );
}
